import { Switch } from '../../../components/ui/Switch.js';
import { LastChanged } from '../parts/LastChanged.js';
import { SettingCard } from '../parts/parts.js';
import {
  BOOKING_EMAIL_EVENTS,
  type BookingEmailEvent,
  type BookingEmails,
} from '../settingsTypes.js';
import type { CardProps } from './cardProps.js';

/**
 * 392 (#1330, mock-up screens 1 and 2): which booking events also email the customer. In-app notifications and push
 * always go and are shown as such. Saved with the tab like every card; a changed row says what it was until saved.
 */
const EVENTS: Record<BookingEmailEvent, { label: string; hint: string }> = {
  confirmed: { label: 'Booking confirmed', hint: 'Paid, or confirmed by staff.' },
  cancelled: { label: 'Booking cancelled', hint: 'By the customer, staff or the platform.' },
  awaiting_payment: {
    label: 'Created by staff, waiting for payment',
    hint: 'Staff booked for the customer; the message carries the way to pay.',
  },
  pickup_reminder: { label: 'Pickup reminder', hint: 'Before the pickup time.' },
  pickup_soon: { label: 'Pickup soon', hint: 'About 30 minutes before pickup.' },
  return_reminder: { label: 'Return reminder', hint: 'Before the return time.' },
  return_soon: { label: 'Return soon', hint: 'About 30 minutes before return.' },
  status_update: {
    label: 'Status update',
    hint: 'Checked out, checked in, completed, when staff move it.',
  },
  changed: { label: 'Changed by staff', hint: 'A different car, time or place.' },
  // 393 (#1313)
  penalty_recorded: {
    label: 'Penalty recorded',
    hint: "Staff recorded a penalty on the customer's booking.",
  },
  // 407 (#1370)
  return_overdue: {
    label: 'Return time passed',
    hint: 'Once, when a rental is not ended by its return time.',
  },
};

export function BookingEmailsCard({ draft, config, latest, refusal }: CardProps) {
  const saved = config.booking_emails ?? null;
  const value: BookingEmails | null = draft.value('booking_emails');
  return (
    <SettingCard
      testId="booking-emails-card"
      title="Booking emails"
      dirty={draft.isDirty('booking_emails')}
      refusal={refusal('booking_emails')}
      description={
        <p>
          Choose which booking events also email the customer. Every event always sends an in-app
          notification (which opens the booking) and a push where the customer allowed it.
        </p>
      }
      footer={<LastChanged setting="booking_emails" latest={latest} />}
    >
      {value === null ? (
        <p className="text-sm text-steel">The booking email settings could not be read.</p>
      ) : (
        <div className="flex flex-col">
          <div className="hidden grid-cols-[minmax(0,1fr)_120px_110px] gap-3 border-b border-hairline pb-2 text-xs font-semibold uppercase tracking-wide text-steel sm:grid">
            <span>Event</span>
            <span>In-app &amp; push</span>
            <span>Email</span>
          </div>
          {BOOKING_EMAIL_EVENTS.map((key) => {
            const { label, hint } = EVENTS[key];
            const on = value[key];
            const changed = saved !== null && saved[key] !== on;
            return (
              <div
                key={key}
                data-testid={`booking-email-${key}`}
                className={[
                  'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-hairline py-3 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_120px_110px]',
                  changed ? 'rounded-md bg-yellow-50 px-2 dark:bg-yellow-500/10' : '',
                ].join(' ')}
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-ink">{label}</div>
                  <div className="text-xs text-steel">
                    {changed ? `Was ${on ? 'off' : 'on'}` : hint}
                  </div>
                </div>
                <span className="hidden justify-self-start rounded bg-green-100 px-2 py-0.5 text-xs text-green-700 sm:inline dark:bg-green-500/15 dark:text-green-300">
                  Always
                </span>
                <div className="flex items-center gap-2 text-sm text-ink">
                  <Switch
                    checked={on}
                    onChange={() => draft.set('booking_emails', { ...value, [key]: !on })}
                    ariaLabel={`${label}: email`}
                  />
                  <span>{on ? 'On' : 'Off'}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SettingCard>
  );
}
